import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import AuthContext from '../context/AuthContext';

const AdminNotices = () => {
  const { admin } = useContext(AuthContext); // ✅ Get Admin Token
  
  const [notices, setNotices] = useState([]);
  
  // ✅ NEW: Track which notice we are editing
  const [editingId, setEditingId] = useState(null);

  const [formData, setFormData] = useState({ 
    title: '', 
    message: '', 
    type: 'General' 
  });

  const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  // Helper for Secure Headers
  const getConfig = () => ({
    headers: { Authorization: `Bearer ${admin?.token}` }
  });

  // 1. Fetch existing notices
  const fetchNotices = async () => {
    try {
      const { data } = await axios.get(`${BASE_URL}/api/notices`);
      setNotices(data);
    } catch (error) { 
      console.error('Error fetching notices:', error); 
    }
  };

  useEffect(() => {
    fetchNotices();
    // eslint-disable-next-line
  }, []);

  // 2. Handle Form Submit (Create OR Update)
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.message) return alert("Please fill all fields");

    try {
      if (editingId) {
        // ✅ UPDATE MODE (PUT)
        await axios.put(`${BASE_URL}/api/notices/${editingId}`, formData, getConfig());
        alert("Notice Updated!");
      } else {
        // ✅ CREATE MODE (POST)
        await axios.post(`${BASE_URL}/api/notices`, formData, getConfig());
        alert("Notice Posted!");
      }

      // Reset Form & State
      setFormData({ title: '', message: '', type: 'General' });
      setEditingId(null);
      fetchNotices(); // Refresh list
    } catch (error) {
      alert("Error saving notice");
    }
  };

  // 3. Handle Edit Click (Fill Form)
  const handleEdit = (notice) => {
    setEditingId(notice._id);
    setFormData({
      title: notice.title,
      message: notice.message,
      type: notice.type
    });
    // Scroll to top so user sees the form
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // 4. Handle Cancel Edit
  const handleCancel = () => {
    setEditingId(null);
    setFormData({ title: '', message: '', type: 'General' });
  };

  // 5. Handle Delete
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this notice?")) return;
    
    try {
      await axios.delete(`${BASE_URL}/api/notices/${id}`, getConfig());
      fetchNotices();
    } catch (error) {
      alert("Error deleting notice");
    }
  };

  return (
    <div className="card shadow-sm mt-4">
      {/* Header changes color based on mode */}
      <div className={`card-header text-white ${editingId ? 'bg-primary' : 'bg-dark'}`}>
        <h5 className="mb-0">
          {editingId ? '✏️ Edit Notice' : '📢 Manage Notice Board'}
        </h5>
      </div>
      
      <div className="card-body">
        
        {/* --- FORM SECTION --- */}
        <form onSubmit={handleSubmit} className="mb-4">
          <div className="row g-2">
            <div className="col-md-4">
              <input 
                type="text" 
                className="form-control" 
                placeholder="Title (e.g., Eid Holidays)"
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
              />
            </div>
            <div className="col-md-3">
              <select 
                className="form-select"
                value={formData.type}
                onChange={(e) => setFormData({...formData, type: e.target.value})}
              >
                <option value="General">General (Green)</option>
                <option value="Urgent">Urgent (Red)</option>
                <option value="Holiday">Holiday</option>
              </select>
            </div>
            <div className="col-md-5">
              <input 
                type="text" 
                className="form-control" 
                placeholder="Short Message..."
                value={formData.message}
                onChange={(e) => setFormData({...formData, message: e.target.value})}
              />
            </div>
          </div>

          <div className="d-flex gap-2 mt-2">
            <button type="submit" className={`btn w-100 ${editingId ? 'btn-primary' : 'btn-success'}`}>
              <i className={`bi ${editingId ? 'bi-check-circle' : 'bi-plus-circle'} me-2`}></i> 
              {editingId ? 'Update Notice' : 'Post Notice'}
            </button>

            {/* Cancel Button (Only shows when editing) */}
            {editingId && (
              <button type="button" onClick={handleCancel} className="btn btn-secondary w-25">
                Cancel
              </button>
            )}
          </div>
        </form>

        <hr />

        {/* --- LIST OF ACTIVE NOTICES --- */}
        <h6 className="text-muted">Active Notices</h6>
        {notices.length === 0 ? (
          <p className="small text-muted">No notices active.</p>
        ) : (
          <ul className="list-group">
            {notices.map((notice) => (
              <li key={notice._id} className={`list-group-item d-flex justify-content-between align-items-center ${editingId === notice._id ? 'bg-light border-primary' : ''}`}>
                <div>
                  <strong>{notice.title}</strong>
                  <span className={`badge ms-2 ${notice.type === 'Urgent' ? 'bg-danger' : 'bg-secondary'}`}>
                    {notice.type}
                  </span>
                  <p className="mb-0 small text-muted">{notice.message}</p>
                </div>
                
                <div className="d-flex gap-2">
                  {/* ✅ EDIT BUTTON */}
                  <button 
                    onClick={() => handleEdit(notice)} 
                    className="btn btn-sm btn-outline-primary"
                    title="Edit Notice"
                  >
                    <i className="bi bi-pencil"></i>
                  </button>

                  {/* DELETE BUTTON */}
                  <button 
                    onClick={() => handleDelete(notice._id)} 
                    className="btn btn-sm btn-outline-danger"
                    title="Delete Notice"
                  >
                    <i className="bi bi-trash"></i>
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default AdminNotices;